import {
  ListOpportunitiesRequestSortField,
  ListOpportunitiesRequestSortOptionInterface as SortOption,
  SortDirection as ListOpportunitiesSortDirection,
} from '@vendasta/sales-opportunities';

export enum PipelineField {
  ACCOUNT = 'account',
  ACTUAL_CLOSE = 'actualClose',
  ASSIGNEE = 'assigneeName',
  ASSIGNED_DATE = 'assignedDate',
  CALLS = 'calls',
  CONNECTIONS = 'connections',
  CREATED = 'created',
  EMAILS = 'emails',
  EXPECTED_CLOSE = 'expectedClose',
  LAST_ACTIVITY = 'lastActivity',
  LAST_CONNECTED = 'lastConnected',
  MEETINGS = 'meetings',
  MONTHLY_REVENUE = 'monthlyRevenue',
  OPPORTUNITY = 'opportunity',
  PIPELINE_NAME = 'pipelineName',
  PIPELINE_STAGE = 'pipelineStage',
  POTENTIAL = 'potentialRevenue',
  STATUS = 'status',
  TOTAL_EFFORTS = 'totalEfforts',
  MEDDIC = 'meddic',
  TYPE = 'type',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export interface PipelineSort {
  active: PipelineField;
  isAscending: boolean;
}

export const defaultSortOption = <PipelineSort>{
  isAscending: false,
  active: PipelineField.POTENTIAL,
};

export function buildSortOptions(sort: PipelineSort): SortOption {
  switch (sort.active) {
    case PipelineField.POTENTIAL:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.PROJECTED_FIRST_YEAR_VALUE,
      };
    case PipelineField.CREATED:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.CREATED,
      };
    case PipelineField.EXPECTED_CLOSE:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.EXPECTED_CLOSE,
      };
    case PipelineField.OPPORTUNITY:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.NAME,
      };
    case PipelineField.LAST_ACTIVITY:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.LAST_ACTIVITY,
      };
    case PipelineField.LAST_CONNECTED:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.LAST_CONNECTED,
      };
    case PipelineField.ACTUAL_CLOSE:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.ACTUAL_CLOSE,
      };
    case PipelineField.PIPELINE_STAGE:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.PIPELINE_STAGE,
      };
    case PipelineField.ASSIGNED_DATE:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.ASSIGNED_DATE,
      };
    case PipelineField.MEDDIC:
      return {
        direction: sort.isAscending ? 0 : 1,
        field: ListOpportunitiesRequestSortField.MEDDIC,
      };
    default:
      console.error('Attempted sort on unknown column: ', sort.active);
  }
}

export const defaultPipelineSortOption = PipelineField.OPPORTUNITY;

export function convertSortOptionToPipelineSort(o: SortOption): PipelineSort {
  const p = <PipelineSort>{};
  switch (o.field) {
    case ListOpportunitiesRequestSortField.PROJECTED_FIRST_YEAR_VALUE:
      p.active = PipelineField.POTENTIAL;
      break;
    case ListOpportunitiesRequestSortField.CREATED:
      p.active = PipelineField.CREATED;
      break;
    case ListOpportunitiesRequestSortField.EXPECTED_CLOSE:
      p.active = PipelineField.EXPECTED_CLOSE;
      break;
    case ListOpportunitiesRequestSortField.NAME:
      p.active = PipelineField.OPPORTUNITY;
      break;
    case ListOpportunitiesRequestSortField.LAST_ACTIVITY:
      p.active = PipelineField.LAST_ACTIVITY;
      break;
    case ListOpportunitiesRequestSortField.LAST_CONNECTED:
      p.active = PipelineField.LAST_CONNECTED;
      break;
    case ListOpportunitiesRequestSortField.ACTUAL_CLOSE:
      p.active = PipelineField.ACTUAL_CLOSE;
      break;
    case ListOpportunitiesRequestSortField.PIPELINE_STAGE:
      p.active = PipelineField.PIPELINE_STAGE;
      break;
    case ListOpportunitiesRequestSortField.MEDDIC:
      p.active = PipelineField.MEDDIC;
      break;
    case ListOpportunitiesRequestSortField.ASSIGNED_DATE:
      p.active = PipelineField.ASSIGNED_DATE;
      break;
    default:
      p.active = defaultPipelineSortOption;
      break;
  }
  p.isAscending = o.direction === ListOpportunitiesSortDirection.ASCENDING;

  return p;
}
